<template>
  <div class="heavy-dom-section">
    <section class="pt-0 print-no-vertical">
      <p
        v-if="activeSuite && activeSuite.company && (showTitle || user.isAdmin)"
        class="title is-4 print-pad-top"
      >
        {{ activeSuite.company.name }} <br />
        <span
          class="title is-6"
          v-if="
            activeSuite.company && activeSuite.company.name !== activeSuite.name
          "
        >
          {{ activeSuite.name }}
        </span>
      </p>
      <div v-if="!isReport" class="schedule-legend mb-4">
        <span class="legend-label is-semibold">
          LEGEND
        </span>
        <div class="level">
          <p class="level-item has-text-centered">
            <button class="button is-small is-text" disabled>
              <span class="icon is-small">
                <i class="far fa-calendar-check" aria-hidden="true"></i>
              </span>
              <span>Appointment is scheduled</span>
            </button>
          </p>
          <p class="level-item has-text-centered">
            <button class="button is-small is-text" disabled>
              <span class="icon is-small">
                <i class="fas fa-reply" aria-hidden="true"></i>
              </span>
              <span>Awaiting your response</span>
            </button>
          </p>
          <p class="level-item has-text-centered">
            <button class="button is-small is-text" disabled>
              <span class="icon is-small">
                <i class="far fa-hourglass" aria-hidden="true"></i>
              </span>
              <span>Request waiting for approval</span>
            </button>
          </p>
        </div>
      </div>
    </section>
    <section
      class="my-8 print-no-vertical"
      v-for="meetingDate in sortedActiveScheduleKeys"
      :key="meetingDate"
    >
      <div class="flex justify-items-start items-center mb-6">
        <img
          v-if="activeSuite.logo_path"
          style="border-radius:3px;"
          class="object-contain block mr-4 w-10 lg:w-16 h-10 lg:h-16 m-0"
          :src="fixHttpProtocol(activeSuite.logo_path)"
        />

        <h3 class="media-content lg:text-lg subtitle is-5 is-bold">
          {{ formatDate(meetingDate) }}
        </h3>
      </div>

      <div class="box print-no-vertical">
        <table class="table is-hoverable is-fullwidth table-with-data-labels">
          <tr class="is-hidden-print">
            <th scope="col" colspan="3">Time Slot</th>
          </tr>
          <tr
            v-for="(timeslot, tsIndex) in activeSchedule[meetingDate]"
            :key="tsIndex"
            :style="{}"
            :class="{
              blocked: !timeslot.is_open && timeslot.scheduled_with === ''
            }"
          >
            <td data-label="Time" style="width:30%" class="align-middle">
              {{
                `${timeslot.start_time} - ${timeslot.end_time} ${displayedTimezone}`
              }}
            </td>
            <td data-label="" class="align-middle">
              <div
                v-if="
                  isReport &&
                    !timeslot.is_open &&
                    timeslot.scheduled_with === ''
                "
                class="show-blocked"
              >
                BLOCKED
              </div>
              <div
                v-if="timeslot.scheduled_with !== ''"
                class="appointment-actions"
              >
                <appointment-actions
                  :is-suite-writeable="isSuiteWriteable"
                  :meeting-id="id"
                  :suite-id="timeslot.scheduled_with"
                  :timeslot="timeslot"
                  type="scheduled"
                  class="mb-2 p-0 lg:mb-0"
                  @cancel="
                    showCancelOrReschedule(
                      timeslot,
                      timeslot.scheduled_with,
                      meetingDate,
                      'scheduled'
                    )
                  "
                  @addToCalendar="addToCalendar($event, meetingDate, timeslot)"
                  @addGuest="addGuest"
                >
                </appointment-actions>
                <template
                  v-if="
                    user.isAdmin && timeslot.guests && timeslot.guests.length
                  "
                >
                  <b class="block mb-0 mt-1">Guests:</b>
                  <p
                    v-for="(guest, guestIndex) in timeslot.guests"
                    :key="guestIndex"
                  >
                    {{ guest.name }}
                    <a @click.prevent="deleteGuest(timeslot, guest.id)"
                      >(remove)</a
                    >
                  </p>
                </template>
              </div>
              <div v-if="!isReport" class="lg:flex flex-wrap">
                <appointment-actions
                  v-for="(pending, pendingIdx) in timeslot.pending"
                  :key="pendingIdx"
                  :is-suite-writeable="isSuiteWriteable"
                  :meeting-id="id"
                  :suite-id="pending.suiteId"
                  :timeslot="timeslot"
                  :type="pending.type"
                  @approve="
                    scheduleMeeting(
                      timeslot,
                      pending.suiteId,
                      meetingSuites[pending.suiteId].name
                    )
                  "
                  @cancel="
                    showCancelOrReschedule(
                      timeslot,
                      pending.suiteId,
                      meetingDate,
                      'pending'
                    )
                  "
                  class="my-2 p-0 first:ml-0 ml-0 lg:ml-2"
                >
                </appointment-actions>
              </div>
            </td>
            <td
              data-label=""
              class="has-text-right is-hidden-print align-middle"
            >
              <template
                v-if="!timeslot.is_open && timeslot.scheduled_with === ''"
              >
                <button
                  type="button"
                  class="button is-small is-outlined"
                  :disabled="!canChangeBlock"
                  @click.prevent="setUnblock(timeslot)"
                >
                  <svg
                    class="svg-inline--fa fa-lock fa-w-14 mr-2"
                    aria-hidden="true"
                    role="img"
                  >
                    <use xlink:href="#icon-unlock"></use>
                  </svg>
                  Unblock
                </button>
              </template>
              <template v-else>
                <button
                  :title="getMouseoverText(timeslot)"
                  :disabled="
                    timeslot.scheduled_with !== '' ||
                      timeslot.pending.length > 0 ||
                      !canChangeBlock
                  "
                  type="button"
                  class="button is-small is-outlined"
                  @click.prevent="setBlock(timeslot)"
                >
                  <svg
                    class="svg-inline--fa fa-lock fa-w-14 mr-2"
                    aria-hidden="true"
                    role="img"
                  >
                    <use xlink:href="#icon-lock"></use>
                  </svg>
                  Block
                </button>
              </template>
            </td>
          </tr>
        </table>
      </div>
    </section>
    <div class="print-page-break"></div>
    <modal
      title="Cancel/Reschedule"
      v-if="showRescheduleModal"
      :show-close-button="false"
    >
      <h5 class="title is-5">
        {{
          meetingSuites[rescheduleData.suiteId]
            ? getCompanySuiteName(
                meetingSuites[rescheduleData.suiteId].name,
                meetingSuites[rescheduleData.suiteId].company.name
              )
            : ''
        }}
      </h5>
      <h6 class="subtitle is-6">
        {{ formatDate(rescheduleData.meetingDate) }}
        {{ rescheduleData.timeslot.start_time }}
      </h6>

      <div class="field">
        <label class="label">What would you like to do?</label>
        <div class="control">
          <label class="radio">
            <input
              type="radio"
              v-model="rescheduleData.cancelOrReschedule"
              name="cancelOrReschedule"
              value="cancel"
            />
            Cancel
          </label>
          <label class="radio">
            <input
              type="radio"
              v-model="rescheduleData.cancelOrReschedule"
              name="cancelOrReschedule"
              value="reschedule"
            />
            Cancel and request a new time
          </label>
        </div>
      </div>

      <div v-if="rescheduleData.cancelOrReschedule === 'reschedule'">
        <reschedule-slots
          v-for="meetingDate in sortedActiveScheduleKeys"
          :key="meetingDate"
          :reschedule-data="rescheduleData"
          :user-schedule="activeSchedule"
          :slots="rescheduleData.suiteSchedule[meetingDate]"
          :meeting-date="formatDate(meetingDate)"
        >
        </reschedule-slots>
      </div>

      <div class="field mt-4">
        <div class="control">
          <textarea
            v-model="rescheduleData.notes"
            class="textarea"
            placeholder="Include an optional note"
          ></textarea>
        </div>
      </div>

      <div slot="footer">
        <button
          type="button"
          class="button is-danger"
          @click="cancelOrReschedule"
          :class="{ 'is-loading': rescheduleData.isLoading }"
          :disabled="
            rescheduleData.isLoading ||
              (rescheduleData.cancelOrReschedule !== 'cancel' &&
                rescheduleData.cancelOrReschedule !== 'reschedule')
          "
        >
          Cancel/Reschedule
        </button>
        <button
          type="button"
          class="button"
          @click="showRescheduleModal = false"
          :disabled="rescheduleData.isLoading"
        >
          Nevermind
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import AppointmentActions from '../shared/AppointmentActions';
import RescheduleSlots from '../shared/RescheduleSlots';
import Modal from '../shared/Modal';
import swal from 'sweetalert';
import { mapGetters } from 'vuex';
import {
  db,
  cancelRequest,
  scheduleMeeting,
  cancelScheduled,
  rejectRequest,
  requestAppt
} from '@/services/firebase';
import dbhelpers from '@/services/dbhelpers';
import {
  formatDate,
  getTimeslot,
  getCompanySuiteName,
  fixHttpProtocol,
  addToCalendar,
  createDateFromTimeString,
  createDateFromDateString,
  convertMeetingTimezoneToShortName
} from '@/services/utils';

export default {
  data() {
    return {
      showModal: false,
      showRescheduleModal: false,
      rescheduleData: {
        suiteId: '',
        suiteSchedule: {},
        newRequestSlotId: '',
        meetingDate: '',
        timeslot: {},
        cancelOrReschedule: null,
        notes: '',
        isLoading: false
      },
      suites: {},
      meeting: {},
      allSuites: [],
      activeSuite: {}
    };
  },
  props: {
    id: { type: String },
    suiteId: { type: String },
    isSuiteWriteable: { type: Boolean, default: false },
    showTitle: { type: Boolean, default: false },
    isReport: { type: Boolean, default: false }
  },
  computed: {
    ...mapGetters(['meetingSuites', 'user']),
    activeSchedule() {
      return Object.keys(this.activeSuite).length &&
        Object.keys(this.activeSuite.appointment_slots).length
        ? this.activeSuite.appointment_slots
        : this.meeting.appointment_slots;
    },
    sortedActiveScheduleKeys() {
      if (!this.activeSchedule) return [];
      const keys = Object.keys(this.activeSchedule) || [];
      return keys.sort();
    },
    canChangeBlock() {
      return this.isSuiteWriteable && this.meeting.phase_index === 2;
    },
    displayedTimezone() {
      return convertMeetingTimezoneToShortName(
        this.meeting && this.meeting.timezone
          ? this.meeting.timezone
          : 'US/Eastern'
      );
    }
  },
  methods: {
    fixHttpProtocol,
    getCompanySuiteName,
    formatDate,
    createDateFromTimeString,
    createDateFromDateString,
    addToCalendar(type, apptDate, timeslot) {
      let scheduledWith = this.getCompanySuiteName(
        this.meetingSuites[timeslot.scheduled_with].name,
        this.meetingSuites[timeslot.scheduled_with].company.name
      );
      let scheduledWithDescription =
        this.meetingSuites[timeslot.scheduled_with].description || '';
      let calendarData = {
        type: type,
        start: createDateFromTimeString(
          apptDate,
          timeslot.start_time,
          this.meeting.timezone
        ),
        end: createDateFromTimeString(
          apptDate,
          timeslot.end_time,
          this.meeting.timezone
        ),
        location: this.meeting.location,
        // changed for ABC 2021. change back to the above after.
        // location: 'https://abc.fmi.org',
        description: `Appointment with ${scheduledWith}`,
        notes: this.meeting.name + '\n\n' + scheduledWithDescription
      };
      addToCalendar(calendarData);
    },
    updateAppointmentSlots(suiteId, scheduleData) {
      if (!suiteId) suiteId = this.suiteId;
      if (!scheduleData) scheduleData = this.activeSchedule;
      // first make sure 'unavailable' type does not get inserted into the database.
      Object.keys(scheduleData).forEach(day => {
        scheduleData[day].forEach(slot => {
          slot.pending.forEach(pendingRequest => {
            if (pendingRequest.type === 'unavailable') {
              pendingRequest.type = 'from';
            }
          });
        });
      });

      this.$firestoreRefs.allSuites
        .doc(suiteId)
        .update({ appointment_slots: scheduleData })
        .catch(error => console.log(error));
    },
    showCancelOrReschedule(timeslot, requestTo, meetingDate, appointmentType) {
      this.rescheduleData = {
        appointmentType,
        suiteId: requestTo,
        suiteSchedule: {},
        newRequestSlotId: '',
        meetingDate,
        timeslot,
        cancelOrReschedule: null,
        notes: '',
        isLoading: false
      };
      dbhelpers
        .getSuiteActiveSchedule(this.meeting, requestTo)
        .then(slots => {
          this.rescheduleData.suiteSchedule = slots;
        })
        .catch(noSlots => {
          this.rescheduleData.suiteSchedule = this.meeting.appointment_slots;
        });
      this.showRescheduleModal = true;
    },
    cancelOrReschedule() {
      this.rescheduleData.isLoading = true;

      const cancelReq =
        this.rescheduleData.appointmentType === 'scheduled'
          ? this.cancelScheduled(
              this.rescheduleData.timeslot.id,
              this.rescheduleData.suiteId
            )
          : this.rejectRequest(
              this.rescheduleData.timeslot.id,
              this.rescheduleData.suiteId
            );

      const requests = [cancelReq];
      let successTitle = 'Appointment Canceled';
      let successMessage = 'The appointment was successfully canceled.';

      if (this.rescheduleData.cancelOrReschedule === 'reschedule') {
        requests.push(this.rescheduleAppointment());
        successTitle = 'Appointment Rescheduled';
        successMessage =
          'The appointment was successfully canceled and a new request was made.';
      }

      Promise.all(requests)
        .then(resp => {
          this.rescheduleData.isLoading = false;
          this.showRescheduleModal = false;
          swal({
            title: successTitle,
            text: successMessage,
            buttons: {
              confirm: {
                text: 'OK',
                value: true,
                className: 'button is-inverted',
                closeModal: true
              }
            }
          });
        })
        .catch(err => {
          console.error(err);
          this.rescheduleData.isLoading = false;
          swal({
            icon: 'error',
            text: 'There was an error processing the cancellation request.',
            buttons: {
              confirm: {
                text: 'OK',
                value: true,
                className: 'button is-inverted',
                closeModal: true
              }
            }
          });
        });
    },
    cancelRequest(appointmentId, requestTo) {
      return cancelRequest({
        meetingId: this.id,
        appointmentId: appointmentId,
        requestFrom: this.suiteId,
        requestTo: requestTo,
        notes: this.rescheduleData.notes
      });
    },
    rejectRequest(appointmentId, requestTo) {
      return rejectRequest({
        meetingId: this.id,
        appointmentId: appointmentId,
        requestFrom: this.suiteId,
        requestTo: requestTo,
        notes: this.rescheduleData.notes
      });
    },
    scheduleMeeting(timeslot, requestTo, suiteName) {
      swal({
        title: `Confirm Appointment Request`,
        text: `Would you like to approve the appointment request with ${suiteName}?`,
        buttons: {
          cancel: {
            text: 'Nevermind',
            value: false,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, approve this appointment',
            value: true,
            visible: true,
            className: 'button is-primary',
            closeModal: false
          }
        }
      })
        .then(proceed => {
          if (!proceed) throw new Error(null);

          return scheduleMeeting({
            meetingId: this.id,
            appointmentId: timeslot.id,
            requestFrom: this.suiteId,
            requestTo: requestTo
          });
        })
        .then(resp => {
          swal({
            title: 'Appointment Approved',
            text: 'The appointment request was approved.',
            icon: 'success'
          });
        })
        // eslint-disable-next-line
        .catch(err => {
          swal.stopLoading();
          this.makeAppointmentUnavailable(timeslot, requestTo);
          swal.close();
        });
    },
    cancelScheduled(appointmentId, requestTo) {
      return cancelScheduled({
        meetingId: this.id,
        appointmentId: appointmentId,
        requestFrom: this.suiteId,
        requestTo: requestTo
      });
    },
    setBlock(timeslot) {
      swal({
        title: `Are you sure?`,
        text: `You are blocking a timeslot (${timeslot.start_time} - ${timeslot.end_time}). You will not receive appointment requests for this time.`,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, block',
            value: true,
            visible: true,
            className: 'button is-danger is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          timeslot.is_open = false;
          this.updateAppointmentSlots();
        }
      });
    },
    setUnblock(timeslot) {
      swal({
        title: `Are you sure?`,
        text: `You are unblocking a timeslot (${timeslot.start_time} - ${timeslot.end_time}). You will now be able to receive appointment requests for this time.`,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, unblock',
            value: true,
            visible: true,
            className: 'button is-primary is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          timeslot.is_open = true;
          this.updateAppointmentSlots();
        }
      });
    },
    rescheduleAppointment() {
      return requestAppt({
        meetingId: this.id,
        appointmentId: this.rescheduleData.newRequestSlotId,
        requestFrom: this.suiteId,
        notes: this.rescheduleData.notes,
        requestTo: this.rescheduleData.suiteId
      });
    },
    getMouseoverText(timeslot) {
      if (timeslot.scheduled_with !== '') {
        return 'You cannot block this timeslot while you have a scheduled meeting.';
      }
      if (timeslot.pending.length > 0) {
        return 'You cannot block this timeslot until you cancel or decline pending requests.';
      }
      return '';
    },
    makeAppointmentUnavailable(timeslot, suiteId) {
      timeslot.pending.filter(
        item => item.suiteId === suiteId && item.type === 'from'
      )[0].type = 'unavailable';
    },
    addGuest(timeslot, id, name) {
      // "Add guest" allows admins to add third party individuals who are registered
      // for the meeting in MatrixMaxx to the appointment. This is only used for
      // meetings that are integrated with BeSpeake so we can programatically
      // add other users to the meeting when we import the schedules.
      if (!this.user.isAdmin) return;
      if (!timeslot.guests) {
        timeslot.guests = [];
      }
      if (!timeslot.guests.some(guest => guest.id === id)) {
        timeslot.guests.push({
          id,
          name
        });
      }
      // need to also update guests on other suite's schedule
      const otherSchedule = this.meetingSuites[timeslot.scheduled_with];
      const otherSlot = getTimeslot(
        timeslot.id,
        otherSchedule.appointment_slots
      );
      otherSlot.guests = timeslot.guests;
      this.updateAppointmentSlots();
      this.updateAppointmentSlots(
        timeslot.scheduled_with,
        otherSchedule.appointment_slots
      );
    },
    deleteGuest(timeslot, id) {
      if (!this.user.isAdmin) return;
      timeslot.guests = timeslot.guests.filter(guest => guest.id !== id);
      // need to also update guests on other suite's schedule
      const otherSchedule = this.meetingSuites[timeslot.scheduled_with];
      const otherSlot = getTimeslot(
        timeslot.id,
        otherSchedule.appointment_slots
      );
      otherSlot.guests = timeslot.guests;
      this.updateAppointmentSlots();
      this.updateAppointmentSlots(
        timeslot.scheduled_with,
        otherSchedule.appointment_slots
      );
    }
  },
  components: {
    AppointmentActions,
    Modal,
    RescheduleSlots
  },
  firestore() {
    let meeting = db.collection('meetings').doc(this.id);
    return {
      meeting,
      allSuites: meeting.collection('suites')
    };
  },
  created() {
    db.collection('meetings')
      .doc(this.id)
      .collection('suites')
      .doc(this.suiteId)
      .onSnapshot(snap => {
        this.activeSuite = { id: snap.id, ...snap.data() };
        const slots = this.activeSuite.appointment_slots
          ? Object.keys(this.activeSuite.appointment_slots)
          : [];

        if (!slots || !slots.length) {
          this.updateAppointmentSlots();
        }
      });
  }
};
</script>

<style>
tr.scheduled td {
  background-color: #99ffbd;
  transition: 0.4s;
}
tr.blocked td {
  color: #a9a9a9;
  background-color: #e8e8e8;
  transition: 0.4s;
}
td {
  transition: 0.4s;
}
.subtitle.is-bold {
  font-weight: 500;
}
.message-body {
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
}

.show-blocked {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/attendees/Schedule.vue